import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, ExternalLink, Loader2, Plus, Server } from 'lucide-react';
import { api } from '../api/client.js';
import { Button } from '../components/Button.jsx';
import { Card, CardHeader } from '../components/Card.jsx';
import { Banner } from '../components/Banner.jsx';
import { Pill } from '../components/Pill.jsx';
import { Table, THead, TBody, TR, TH, TD, Empty } from '../components/Table.jsx';
import { StaticSites } from './StaticSites.jsx';

/**
 * Sites — §13 Phase C, the reference page (the mockup shows this one).
 *
 * ⚠️ The mockup's table has eight columns; five of them (Stack, PHP, Disk, Last
 * backup, live Status) have no data source. `GET /api/sites` returns the
 * vps1-api registry row (id, domain, site_name, client_name, admin_email,
 * site_dir, created_at) merged with panel metadata (plan, subscription_status,
 * monthly_price_cents, currency, panel_notes) — nothing about containers, PHP
 * builds, disk usage or backups. Getting those needs either a wider vps1-api
 * list response or one request per row, and §7a rules out endpoint changes in a
 * restyle. So this renders the mockup's table *treatment* over the columns that
 * exist, and Status shows the **subscription**, labelled as such. Faking a
 * "Live" pill from `subscription_status` was the tempting shortcut and is
 * exactly the lie §6 warns about.
 *
 * They stay hidden rather than dashed or zeroed: an empty column tells the
 * truth, a plausible-but-wrong "Last backup" is the one the operator relies on
 * precisely on the day it matters.
 *
 * What each one would actually need, so nobody has to re-derive it (all five
 * are vps1-api work — a wider `GET /api/sites`, not a panel change):
 *
 *   Stack        docker inspect on the WP container → image + whether it is
 *                the Docker stack or host PHP-FPM. Today only /:domain/status
 *                does a docker inspect, and only for one site at a time. Also
 *                the gate for the mockup's All / Docker / Host PHP-FPM /
 *                Suspended tabs, which are absent for the same reason.
 *   PHP          `php -v` (or the image tag) inside the WP container — same
 *                per-container inspect as Stack; comes free once that exists.
 *   Disk         `du -sh` of site_dir + the named volume's mount source.
 *                Costly per row, so it wants caching in sites.db rather than
 *                being computed on every list.
 *   Last backup  newest entry from listBackups(domain) — the data exists
 *                already, but only per-site; the list endpoint would need to
 *                fold in one mtime per site (cheap, a readdir per site).
 *   Status       the WP container's real running state, i.e. the docker
 *                inspect again, plus `.maintenance` for suspended. This is the
 *                one that must never be inferred from subscription_status.
 *
 * Also not carried over from the mockup, for the same reason: the checkbox
 * column and the bulk "Delete" / "Run backup" header actions (no bulk endpoint
 * exists — delete is per-domain and gated on `confirm_domain`).
 */
export function Sites() {
  const [state, setState] = useState({ status: 'loading' });

  useEffect(() => {
    api('/api/sites')
      .then((data) => setState({ status: 'ok', data }))
      .catch((err) => setState({ status: 'error', message: err.message }));
  }, []);

  const sites = state.data?.sites ?? [];

  return (
    <div>
      {state.status === 'error' && (
        <Banner kind="error" title="Couldn't load the site list" className="mb-5">
          {state.message} — nothing has changed; this view only reads. Sites
          themselves are unaffected, and so are their backups.
        </Banner>
      )}

      <Card>
        <CardHeader
          title="WordPress sites"
          chip={state.status === 'ok' ? String(sites.length) : undefined}
          description={
            state.status === 'ok' && state.data?.source
              ? `Registered on the WordPress agent, merged with panel metadata. Source: ${state.data.source}.`
              : 'Registered on the WordPress agent, merged with panel metadata.'
          }
          actions={
            <Button as={Link} to="/sites/new" variant="primary" icon={Plus}>
              Create site
            </Button>
          }
        />

        {state.status === 'loading' && <LoadingRow />}
        {state.status === 'error' && <ErrorRow />}
        {state.status === 'ok' && sites.length === 0 && (
          <EmptyRow message={state.data?.message} />
        )}
        {state.status === 'ok' && sites.length > 0 && <SitesTable sites={sites} />}
      </Card>

      <StaticSites />
    </div>
  );
}

// ─── States ─────────────────────────────────────────────────────────────────

function LoadingRow() {
  return (
    <div className="flex items-center justify-center gap-2 p-12 text-[13.5px] text-t3">
      <Loader2 className="w-4 h-4 animate-spin" />
      Loading sites…
    </div>
  );
}

function ErrorRow() {
  return (
    <div className="p-12 text-center text-[13.5px] text-t3">
      The list is unavailable. Details are in the notice above.
    </div>
  );
}

function EmptyRow({ message }) {
  return (
    <div className="flex flex-col items-center p-12 text-center">
      <Server className="w-10 h-10 mb-3 text-ic-light" />
      <h3 className="text-[13.5px] font-medium text-t1">No sites yet</h3>
      <p className="mt-1 max-w-sm text-[12.5px] text-t3">
        Sites appear here once they are registered on the WordPress agent.
      </p>
      {message && (
        <p className="mt-3 font-mono text-[12.5px] text-t3">{message}</p>
      )}
    </div>
  );
}

// ─── Table ──────────────────────────────────────────────────────────────────

function SitesTable({ sites }) {
  return (
    <Table>
      <THead>
        <TR hover={false}>
          <TH>Domain</TH>
          <TH>Site name</TH>
          <TH>Client</TH>
          <TH>Plan</TH>
          <TH>Subscription</TH>
          <TH>Admin email</TH>
          <TH>Created</TH>
          <TH className="w-10" />
        </TR>
      </THead>
      <TBody>
        {sites.map((site) => (
          <SiteRow key={site.id ?? site.domain} site={site} />
        ))}
      </TBody>
    </Table>
  );
}

function SiteRow({ site }) {
  const siteUrl = site.domain ? `https://${site.domain}` : null;
  return (
    <TR>
      {/* The mockup styles the identifying cell as plain 500/--color-t1, not
          mono, even though §2 lists hostnames as machine values. Mockup wins:
          this is the column you scan down, and mono at 500 reads cramped. */}
      <TD name>
        {siteUrl ? (
          <a
            href={siteUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 hover:text-blue hover:underline"
          >
            {site.domain}
            <ExternalLink className="w-3 h-3 text-ic-light" />
          </a>
        ) : (
          <Empty />
        )}
      </TD>
      <TD>{site.site_name || <Empty />}</TD>
      <TD>{site.client_name || <Empty />}</TD>
      <TD>{site.plan || <Empty />}</TD>
      <TD>
        {site.subscription_status ? (
          <Pill state={site.subscription_status} />
        ) : (
          <Empty />
        )}
      </TD>
      <TD mono>{site.admin_email || <Empty />}</TD>
      <TD className="whitespace-nowrap">{formatDate(site.created_at)}</TD>
      <TD className="text-right">
        {site.domain && (
          <Link
            to={`/sites/${encodeURIComponent(site.domain)}`}
            className="inline-flex items-center rounded-ctl p-1 text-ic hover:bg-s-3 hover:text-t1"
            title={`Open ${site.domain}`}
            aria-label={`Open ${site.domain}`}
          >
            <ChevronRight className="w-4 h-4" />
          </Link>
        )}
      </TD>
    </TR>
  );
}

function formatDate(value) {
  if (!value) return <Empty />;
  // SQLite "YYYY-MM-DD HH:MM:SS" (no TZ) → treat as UTC.
  const date = new Date(value.includes('T') ? value : value.replace(' ', 'T') + 'Z');
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
